/**
 * Isolates values interpolated into translated messages, so an LTR value such as a phone number,
 * an email, or a Latin name keeps its character order inside an RTL sentence.
 *
 * Use {@link ltr} and {@link auto} for values rendered as JSX. Use {@link ltrText} and
 * {@link autoText} only where the message must be a plain string. Wrap only the text people see,
 * never a value that is parsed or sent.
 *
 * @see docs/i18n-bidirectional-text.md
 */
import React, { ReactNode } from "react"

/**
 * Unicode directional isolate characters, for cases the helpers don't cover, such as isolating a
 * value as RTL. Close every `LRI`, `RLI`, and `FSI` with a `PDI`.
 */
export const DIRECTION_ISOLATES = {
  /** Left-to-right isolate: the content is laid out LTR. */
  LRI: "\u2066",
  /** Right-to-left isolate: the content is laid out RTL. */
  RLI: "\u2067",
  /** First-strong isolate: direction comes from the content's first strong character ("auto"). */
  FSI: "\u2068",
  /** Pop directional isolate: closes the most recent `LRI`, `RLI` or `FSI`. */
  PDI: "\u2069"
} as const

const ISOLATE_INITIATORS: string[] = [DIRECTION_ISOLATES.LRI, DIRECTION_ISOLATES.RLI, DIRECTION_ISOLATES.FSI]

const isEmptyNode = (value: ReactNode): boolean =>
  value === null || value === undefined || value === "" || typeof value === "boolean"

/** True when the first character opens an isolate and the last character closes that same isolate. */
const isWrappedInIsolate = (text: string): boolean => {
  if (text.length < 2 || !ISOLATE_INITIATORS.includes(text[0]) || text[text.length - 1] !== DIRECTION_ISOLATES.PDI) {
    return false
  }
  let depth = 0
  for (let i = 0; i < text.length; i++) {
    if (ISOLATE_INITIATORS.includes(text[i])) {
      depth++
    } else if (text[i] === DIRECTION_ISOLATES.PDI) {
      depth--
      if (depth === 0) {
        return i === text.length - 1
      }
    }
  }
  return false
}

const stripOuterIsolates = (text: string): string => {
  let stripped = text
  while (isWrappedInIsolate(stripped)) {
    stripped = stripped.slice(1, -1)
  }
  return stripped
}

const isolateText = (initiator: string, value: string | number): string => {
  if (value === null || value === undefined || value === "") {
    return value as string
  }
  const inner = stripOuterIsolates(String(value))
  return inner === "" ? inner : `${initiator}${inner}${DIRECTION_ISOLATES.PDI}`
}

/**
 * Isolates a JSX value as left-to-right. Use for values that are always LTR, such as phone
 * numbers, emails, and URLs. Wrap the displayed text, never the `tel:` or `mailto:` href.
 *
 * @param value - `null`, `undefined`, `''`, and booleans are returned unchanged.
 * @returns `<bdi dir="ltr">{value}</bdi>`
 *
 * @example
 * <FormattedMessage
 *   defaultMessage="Talk to a Carrot Companion at {phone}"
 *   values={{ phone: ltr(phoneNumber) }}
 * />
 */
export function ltr(value: ReactNode): ReactNode {
  return isEmptyNode(value) ? value : <bdi dir="ltr">{value}</bdi>
}

/**
 * Isolates a JSX value and takes its direction from its content. Use for free text such as names,
 * so a Latin name renders LTR and a Hebrew name renders RTL.
 *
 * @param value - `null`, `undefined`, `''`, and booleans are returned unchanged.
 * @returns `<bdi>{value}</bdi>`
 *
 * @example
 * <FormattedMessage
 *   defaultMessage="{carrotCompanion} is a general expert on Carrot"
 *   values={{ carrotCompanion: auto(companionName) }}
 * />
 */
export function auto(value: ReactNode): ReactNode {
  return isEmptyNode(value) ? value : <bdi>{value}</bdi>
}

/**
 * String version of {@link ltr}, for props that need a plain string, such as `aria-label`. Wraps
 * the value in `LRI` … `PDI`, replacing any isolate that already wraps the whole value. Never use
 * the result in an href, a form value, or an API payload.
 *
 * @param value - `null`, `undefined`, and `''` are returned unchanged.
 * @returns The value wrapped in `LRI` … `PDI`, or `''` if the value held only isolate characters.
 *
 * @example
 * intl.formatMessage(
 *   { defaultMessage: "Call Carrot at {phone}" },
 *   { phone: ltrText(phoneNumber) }
 * )
 */
export function ltrText(value: string | number): string {
  return isolateText(DIRECTION_ISOLATES.LRI, value)
}

/**
 * String version of {@link auto}, for props that need a plain string, such as `alt`. Wraps the
 * value in `FSI` … `PDI`, replacing any isolate that already wraps the whole value.
 *
 * @param value - `null`, `undefined`, and `''` are returned unchanged.
 * @returns The value wrapped in `FSI` … `PDI`, or `''` if the value held only isolate characters.
 *
 * @example
 * <Avatar
 *   alt={intl.formatMessage({ defaultMessage: "Photo of {name}" }, { name: autoText(companionName) })}
 * />
 */
export function autoText(value: string | number): string {
  return isolateText(DIRECTION_ISOLATES.FSI, value)
}
